import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';

const ShopContainer = styled.div`
    padding: 2rem 10rem;
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        flex-direction: column;
        padding: 2rem 0;
    }
`;

const TabContainer = styled.div`
    display: flex;
    justify-content: left;
    margin-bottom: 2rem;
    flex-wrap: wrap;
`;

const Tab = styled.button<{ $active: boolean }>`
    padding: 0.5rem 1rem;
    font-size: 1rem;
    background-color: ${props => props.$active ? '#4CAF50' : '#f0f0f0'};
    color: ${props => props.$active ? 'white' : 'black'};
    border: none;
    cursor: pointer;
    margin: 0.5rem;

    &:hover {
        background-color: ${props => props.$active ? '#45a049' : '#e0e0e0'};
    }

    @media (max-width: 768px) {
        flex: 1 0 auto;
        font-size: 0.9rem;
        padding: 0.4rem 0.8rem;
    }
`;

const ContentSection = styled.section`
    margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
    font-size: 1.5rem;
    margin-bottom: 1rem;
`;

const Text = styled.p`
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1rem;
`;

const List = styled.ul`
    list-style-type: disc;
    margin-left: 2rem;
    margin-bottom: 1rem;
`;

const ListItem = styled.li`
    font-size: 1rem;
    line-height: 1.6;
`;

const ShopGrid = styled.div`
    display: grid;
    margin-top: 10px;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 2rem;
`;

const ShopCard = styled.div`
    cursor: pointer;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
        transform: translateY(-5px);
    }
`;

const ShopCardImage = styled.img`
    width: 100%;
    height: 300px;
    object-fit: cover;
`;

const ShopInfo = styled.div`
    padding: 1rem;
`;

const ShopTitle = styled.h2`
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
`;

const ShopImageGallery = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
`;

const ShopGalleryImage = styled.img`
    max-width: 100%;
    max-height: 600px;
    object-fit: cover;
    border-radius: 4px;

    @media (max-width: 768px) {
        width: 100%;
        max-height: none;
    }
`;

const Modal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background-color: white;
    padding: 2rem 5rem;
    border-radius: 8px;
    max-width: 60%;
    max-height: 80%;
    overflow-y: auto;
    position: relative;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        max-width: 80%;
        padding: 2rem;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    line-height: 1;
`;

interface ShopItem {
    title: string;
    price: string;
    imageSize: number;
    folderName: string;
}

interface ShopImageGalleryProps {
    selectedItem: {
        title: string;
        folderName: string;
        imageSize: number;
    };
}

const ShopImageGalleryComponent: React.FC<ShopImageGalleryProps> = ({selectedItem}) => {
    const images = [];

    for (let i = 1; i <= selectedItem.imageSize; i++) {
        images.push(
            <ShopGalleryImage
                key={i}
                src={`${process.env.PUBLIC_URL}/shop/${selectedItem.folderName}/${i}.JPG`}
                alt={`${selectedItem.title} 이미지 ${i}`}
            />
        );
    }

    return <ShopImageGallery>{images}</ShopImageGallery>;
};

const Shop: React.FC = () => {
    const { tab } = useParams<{ tab?: string }>();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('notice');
    const [selectedItem, setSelectedItem] = useState<ShopItem | null>(null);

    const products = [
        {title: "꽃다발", folderName: "handtied_bouquet", price: "7만원 이상", imageSize: 23},
        {title: "꽃바구니", folderName: "flower_basket", price: "13만원 이상", imageSize: 22},
        {title: "화병꽃이", folderName: "vase_arrangement", price: "10만원 이상", imageSize: 12},
        {title: "센터피스", folderName: "centerpiece", price: "20만원 이상", imageSize: 35},
        {title: "웨딩부케", folderName: "wedding_bouquet", price: "25만원 이상", imageSize: 16},
    ];
    const silkProducts = [
        {title: "Watercolor Florals Centerpiece", folderName: "silk_flower/watercolor_florals_centerpiece", price: "25만원 이상", imageSize: 4},
        {title: "Sea Whispers Centerpiece", folderName: "silk_flower/sea_whispers_centerpiece", price: "25만원 이상", imageSize: 5},
        {title: "Blue Waves Basket", folderName: "silk_flower/blue_waves_basket", price: "20만원 이상", imageSize: 3},
        {title: "Autumn Breeze Centerpiece", folderName: "silk_flower/autumn_breeze", price: "25만원 이상", imageSize: 2},
    ];

    useEffect(() => {
        if (tab) {
            setActiveTab(tab);
        }
    }, [tab]);

    const handleTabChange = (newTab: string) => {
        setActiveTab(newTab);
        navigate(`/shop/${newTab}`);
    };

    const openModal = (item: ShopItem) => {
        setSelectedItem(item);
    };

    const closeModal = () => {
        setSelectedItem(null);
    };

    return (
        <ShopContainer>
            <h2>Shop</h2>
            <TabContainer>
                <Tab $active={activeTab === 'notice'} onClick={() => handleTabChange('notice')}>공지사항</Tab>
                <Tab $active={activeTab === 'signature'} onClick={() => handleTabChange('signature')}>시그니처 꽃주문</Tab>
                <Tab $active={activeTab === 'silk'} onClick={() => handleTabChange('silk')}>조화</Tab>
                <Tab $active={activeTab === 'project'} onClick={() => handleTabChange('project')}>프로젝트 플라워</Tab>
            </TabContainer>

            {activeTab === 'notice' && (
                <>
                    <ContentSection>
                        <SectionTitle>메이플레르 꽃 주문 안내</SectionTitle>
                        <Text>
                            메이플레르의 꽃 주문 방법에 대한 안내를 드립니다. 모든 상품은 예약제로 운영되며, 최소 3~7일 전 문의가 필요합니다.
                        </Text>
                    </ContentSection>

                    <ContentSection>
                        <SectionTitle>주문 절차</SectionTitle>
                        <List>
                            <ListItem>모든 상품은 예약제로 운영됩니다. (최소 3~7일 전 문의 필요)</ListItem>
                            <ListItem>특정 디자인이나 꽃을 원하실 경우, 일주일 전 주문을 권장합니다.</ListItem>
                            <ListItem>주문은 카카오톡채널 '메이플레르'로만 받습니다. (DM, 전화 불가)</ListItem>
                            <ListItem>생화 특성상 이미지와 실제 상품 간 차이가 있을 수 있으나, 최대한 비슷한 분위기로 제작해 드립니다.</ListItem>
                        </List>
                    </ContentSection>

                    <ContentSection>
                        <SectionTitle>주문 가능 일정</SectionTitle>
                        <List>
                            <ListItem>픽업 및 배송: 수, 목, 금, 토</ListItem>
                            <ListItem>상담 시간: 화~금 9:00~16:00, 토 9:00~15:00 (일, 월은 휴무, 휴무일에는 상담 서비스가 제공되지 않습니다)</ListItem>
                            <ListItem>화요일 주문은 금요일 이후 발송 가능</ListItem>
                        </List>
                    </ContentSection>

                    <ContentSection>
                        <SectionTitle>배송 안내</SectionTitle>
                        <List>
                            <ListItem>정확한 시간 지정은 불가하며, 오전 또는 오후로만 선택 가능</ListItem>
                            <ListItem>배송은 14:00~15:00 이전 출발만 가능</ListItem>
                        </List>
                    </ContentSection>

                    <ContentSection>
                        <SectionTitle>주의사항</SectionTitle>
                        <List>
                            <ListItem>주문 후 7일 전부터는 취소 및 환불이 어려우니 신중히 결정해 주세요.</ListItem>
                            <ListItem>1인 운영 작업실로, 매달 일정이 변동될 수 있습니다. 자세한 사항은 상담을 통해 확인 가능합니다.</ListItem>
                            <ListItem>클래스, 공간 스타일링, 워크샵, 대관 업무도 병행하고 있어 일정 조율이 필요할 수 있습니다.</ListItem>
                        </List>
                        <Text>
                            주문 전 위 내용을 충분히 숙지해 주시기 바랍니다.
                            정성을 다해 아름다운 꽃으로 여러분의 특별한 순간을 준비하겠습니다.
                        </Text>
                    </ContentSection>
                </>
            )}

            {activeTab === 'signature' && (
                <>
                    <ContentSection>
                        <SectionTitle>시그니처 꽃주문 | Signature Flower Order</SectionTitle>
                        <Text>
                            계절의 변화를 담은 다채로운 꽃으로 정성껏 제작해 드립니다.
                        </Text>
                        <List>
                            <ListItem>계절별로 사용되는 꽃이 달라집니다. 아래 이미지는 사진 색감이나 분위기만 참고 부탁드립니다.</ListItem>
                            <ListItem>상품의 정확한 크기는 아래 사이즈표를 확인해 주시기 바랍니다.</ListItem>
                        </List>

                        <ShopGrid>
                            {products.map((item, index) => (
                                <ShopCard key={index} onClick={() => openModal(item)}>
                                    <ShopCardImage
                                        src={`${process.env.PUBLIC_URL}/shop/${item.folderName}/1.JPG`}
                                        alt={item.title}
                                    />
                                    <ShopInfo>
                                        <ShopTitle>{item.title}</ShopTitle>
                                    </ShopInfo>
                                </ShopCard>
                            ))}
                        </ShopGrid>
                    </ContentSection>
                    <ContentSection>
                        <SectionTitle>상품 및 가격</SectionTitle>
                        <List>
                            <ListItem>
                                꽃다발: 7만원 이상
                                <List>
                                    <ListItem>7만원(30x40cm)</ListItem>
                                    <ListItem>10만원(35x40cm)</ListItem>
                                    <ListItem>15만원(40x45cm)</ListItem>
                                    <ListItem>20만원(45x55cm)</ListItem>
                                    <ListItem>25만원(50x55cm)</ListItem>
                                    <ListItem>30만원(60x65cm)</ListItem>
                                </List>
                            </ListItem>
                            <ListItem>
                                꽃바구니: 13만원 이상
                                <List>
                                    <ListItem>13만원(35x40cm)</ListItem>
                                    <ListItem>20만원(50x50cm)</ListItem>
                                    <ListItem>25만원(60x60cm)</ListItem>
                                    <ListItem>30만원(65x65cm)</ListItem>
                                    <ListItem>40만원(70x75cm)</ListItem>
                                </List>
                            </ListItem>
                            <ListItem>
                                화병꽃이: 10만원 이상
                            </ListItem>
                            <br/>
                            <ListItem>
                                센터피스: 20만원 이상
                                <List>
                                    <ListItem>20만원(40x40cm)</ListItem>
                                    <ListItem>25만원(45x45cm)</ListItem>
                                    <ListItem>35만원(55x55cm)</ListItem>
                                    <ListItem>45만원(65x65cm)</ListItem>
                                    <ListItem>55만원(70x80cm)</ListItem>
                                </List>
                            </ListItem>
                            <ListItem>웨딩부케: 25만원 이상</ListItem>
                        </List>
                        <Text>
                            * 크기는 대략적인 기준이며, 사용되는 꽃과 소재에 따라 실제 크기와 볼륨감이 달라질 수 있습니다.
                        </Text>
                    </ContentSection>

                    <ContentSection>
                        <SectionTitle>주문 양식</SectionTitle>
                        <List>
                            <ListItem>신청인 이름 및 연락처</ListItem>
                            <ListItem>꽃 필요 날짜</ListItem>
                            <ListItem>상품 종류</ListItem>
                            <ListItem>원하는 색감 (참고 이미지)</ListItem>
                            <ListItem>선호 금액대</ListItem>
                            <ListItem>픽업 또는 배송 여부</ListItem>
                            <ListItem>픽업) 희망 일자와 시간 (수~금 9~16시, 토 9~14시)</ListItem>
                            <ListItem>배송) 다마스/자동차 퀵 (수~금 9~15시, 토 9~14시)</ListItem>
                        </List>
                        <Text>
                            이용 가능 서비스: 카카오퀵 "차량 픽업 예약", 보르조(Borze), 다마스퀵<br/>
                            주의: 오토바이 퀵 불가
                        </Text>
                    </ContentSection>

                    <ContentSection>
                        <SectionTitle>참고사항</SectionTitle>
                        <Text>
                            계절과 꽃 시장 상황에 따라 사용 가능한 꽃의 종류가 변동될 수 있습니다. 생화의 특성상 완전히 동일한 꽃 조합은 어렵습니다. 원하시는 색감과 분위기에 최대한 가깝게
                            제작해 드립니다.
                        </Text>
                        <Text>
                            원하시는 분위기의 꽃을 준비할 경우, 사용되는 꽃의 종류에 따라 금액이 달라질 수 있습니다. 저렴한 꽃으로는 가격에 맞춰 최대한 풍성한 스타일로 제작이 가능하며,
                            고가의 꽃을 선택하시면 크기는 작지만 고급스러운 느낌을 연출할 수 있습니다. 특별한 디자인이 있으실 경우 알려주시면 상담해드립니다.
                        </Text>
                        <Text>
                            선입금 확인 후 꽃 사입 및 제작이 진행됩니다.
                        </Text>
                        <Text>
                            일요일/월요일은 휴무 (상담 불가)
                        </Text>
                    </ContentSection>

                    <ContentSection>
                        <SectionTitle>주문 시 유의사항</SectionTitle>
                        <List>
                            <ListItem>생화 특성상 이미지와 차이가 있을 수 있으나, 요청한 색감과 분위기에 최대한 맞춰 제작합니다.</ListItem>
                            <ListItem>배송 시 순수 배송비만 청구되며, 거리에 따라 배송비가 상이합니다.</ListItem>
                            <ListItem>주문은 일, 월을 제외한 요일로 해주시고, 충분한 여유를 두고 문의 부탁드립니다.</ListItem>
                            <ListItem>주문은 카카오톡 '메이플레르'로 주문 양식에 따라 연락 주시면 감사하겠습니다.</ListItem>
                        </List>
                    </ContentSection>
                </>
            )}

            {activeTab === 'silk' && (
                <>
                    <ContentSection>
                        <SectionTitle>조화 꽃: 영원히 피어나는 아름다움</SectionTitle>
                        <Text>
                            아름다운 꽃의 모습을 영원히 간직하고 싶은 마음을 담아, 메이플레르에서 특별한 조화 컬렉션을 선보입니다.<br/> 생화와 같은 아름다움을 지닌 조화 꽃으로 시간이
                            지나도 변하지 않는 감동을 선사하며, 집, 샵, 스튜디오 등 다양한 공간 연출에 적합합니다.
                        </Text>
                        <ShopGrid>
                            {silkProducts.map((item, index) => (
                                <ShopCard key={index} onClick={() => openModal(item)}>
                                    <ShopCardImage
                                        src={`${process.env.PUBLIC_URL}/shop/${item.folderName}/1.JPG`}
                                        alt={item.title}
                                    />
                                    <ShopInfo>
                                        <ShopTitle>{item.title}</ShopTitle>
                                    </ShopInfo>
                                </ShopCard>
                            ))}
                        </ShopGrid>
                    </ContentSection>
                    <ContentSection>
                        <SectionTitle>제품 안내</SectionTitle>
                        <List>
                            <ListItem>가격: 25만원 이상</ListItem>
                            <ListItem>
                                Size:
                                <List>
                                    <ListItem>1) 25만원(45x45cm)</ListItem>
                                    <ListItem>2) 35만원(55x55cm)</ListItem>
                                    <ListItem>3) 45만원(65x65cm)</ListItem>
                                    <ListItem>4) 55만원(70x80cm)</ListItem>
                                </List>
                            </ListItem>
                            <ListItem>제작 기간: 10~20일 소요</ListItem>
                            <ListItem>초기에는 샘플 상품 및 기본 디자인 제공</ListItem>
                            <ListItem>추후 커스터마이징 옵션 추가 예정 (추가 비용 발생 가능)</ListItem>
                        </List>
                        <Text>
                            * 크기는 대략적인 기준이며, 사용되는 꽃과 소재에 따라 실제 크기와 볼륨감이 달라질 수 있습니다.
                        </Text>
                        <SectionTitle>주문 시 유의사항</SectionTitle>
                        <List>
                            <ListItem>맞춤 제작 상품으로, 주문 확정 후 취소 및 환불이 불가합니다.</ListItem>
                            <ListItem>정성스런 제작에 시간이 소요되니, 여유 있게 주문해 주시기 바랍니다.</ListItem>
                        </List>
                        <Text>
                            메이플레르의 조화 센터피스로 공간에 화사함을 더합니다.<br/>집안 인테리어의 우아한 포인트부터 상업 공간의 고급스러운 장식까지, 사계절 내내 변함없는 아름다움으로
                            특별한 분위기를 연출합니다.
                        </Text>
                    </ContentSection>
                </>
            )}

            {activeTab === 'project' && (
                <>
                    <ContentSection>
                        <SectionTitle>프로젝트 플라워: 계절을 담은 아름다움</SectionTitle>
                        <Text>
                            프로젝트 플라워는 시간의 흐름과 함께 변화하는 자연의 아름다움을 담았습니다.<br/>각 계절의 고유한 매력을 담아낸 꽃 작품들을 시즌 한정 상품으로
                            선보입니다.<br/>엄선된 꽃으로 제작된 이 특별한 작품들을 10~20% 할인된 가격으로 만나보실 수 있습니다.
                        </Text>
                        <Text>
                            계절마다 다채로운 꽃이 등장하고 사라지며, 그 시기가 지나면 긴 기다림이 필요합니다.<br/>찰나의 계절을 담은 특별한 꽃으로 삶의 공간에 아름다움을 더해보세요.
                        </Text>
                        <Text>
                            <b>* 11~12월 크리스마스 시즌 상품 판매 예정입니다.</b>
                        </Text>
                    </ContentSection>
                </>
            )}

            {selectedItem && (
                <Modal onClick={closeModal}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <CloseButton onClick={closeModal}>&times;</CloseButton>
                        <h2>{selectedItem.title}</h2>
                        <ShopImageGalleryComponent selectedItem={selectedItem}/>
                    </ModalContent>
                </Modal>
            )}

        </ShopContainer>
    );
};

export default Shop;